import CalendarIcon from "@mui/icons-material/CalendarTodayRounded";
import NumbersIcon from "@mui/icons-material/NumbersRounded";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import UserWithAvatar from "@entrilia/shared/components/UserWithAvatar";
import CubeIcon from "@entrilia/shared/icons/CubeIcon";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { workflowTypeCaptionMap } from "@entrilia/shared/utilities/enumCaptions";
import type { InvestorChangeRequestDetails } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import CardAccountDetailsIcon from "@entrilia/shared/icons/CardAccountDetailsIcon";
import { pageRoutes } from "../../../../routes";
import InlineAttribute from "../../../common/InlineAttribute";
import UserWithTimestamp from "../../../common/UserWithTimestamp";
import { hasApprovalInfo, isV2ChangeRequest } from "../changeRequestsFormatters";
import WorkflowStatusTag from "../WorkflowStatusTag";

interface Props {
  changeRequestDetails: InvestorChangeRequestDetails;
}

const ChangeRequestInfoSection = ({ changeRequestDetails }: Props) => {
  const { clientCode, hasAnyPermission } = useClientContext();

  const isV2 = isV2ChangeRequest(changeRequestDetails);
  const hasAccessToFundStructure = hasAnyPermission(["ViewFundStructure", "ManageFundStructure"]);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Request Info</Typography>
      {!isV2 && (
        <InlineAttribute
          Icon={NumbersIcon}
          label="Reference"
          labelWidth="8.5rem"
          value={changeRequestDetails.referenceCode}
        />
      )}
      <InlineAttribute
        Icon={CardAccountDetailsIcon}
        label="Investor"
        labelWidth="8.5rem"
        value={
          hasAccessToFundStructure ? (
            <Link
              component={RouterLink}
              to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investors}/${changeRequestDetails.investorId}/main`}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              {changeRequestDetails.investorName}
            </Link>
          ) : (
            changeRequestDetails.investorName
          )
        }
      />
      {!isV2 && (
        <>
          <InlineAttribute
            Icon={PersonIcon}
            label="Requested by"
            labelWidth="8.5rem"
            value={
              <UserWithTimestamp
                userName={changeRequestDetails.requestedByUserName}
                timestamp={changeRequestDetails.requestedAt}
              />
            }
          />
          <InlineAttribute
            Icon={CubeIcon}
            label="Type"
            labelWidth="8.5rem"
            value={workflowTypeCaptionMap[changeRequestDetails.workflowType] ?? changeRequestDetails.workflowType}
          />
          <InlineAttribute
            Icon={NumbersIcon}
            label="Status"
            labelWidth="8.5rem"
            value={<WorkflowStatusTag workflowStatus={changeRequestDetails.workflowStatus} />}
          />
          <InlineAttribute
            Icon={PersonIcon}
            label="Reviewed by"
            labelWidth="8.5rem"
            value={
              hasApprovalInfo(changeRequestDetails.workflowStatus) &&
              changeRequestDetails.approvalStatusUpdatedByUserName && (
                <UserWithAvatar userName={changeRequestDetails.approvalStatusUpdatedByUserName} avatarSize="small" />
              )
            }
          />
          <InlineAttribute
            Icon={CalendarIcon}
            label="Reviewed at"
            labelWidth="8.5rem"
            value={
              hasApprovalInfo(changeRequestDetails.workflowStatus) &&
              changeRequestDetails.approvalStatusUpdatedAt &&
              convertISODateTime(changeRequestDetails.approvalStatusUpdatedAt)
            }
          />
        </>
      )}
    </Stack>
  );
};

export default ChangeRequestInfoSection;
